import { Box, Dialog, DialogActions, DialogContent, IconButton, Typography, useTheme } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { SiTicktick } from 'react-icons/si';
import Button from '@/components/Common/Button';
import { VoiceEngine } from '@/apis/agency/voiceEngines/types';
import SparkleIcon from '@/assets/icons/SparkleIcon';

export interface VoiceEngineUpdateDetailsModalProps {
  open: boolean;
  onClose: () => void;
  engine: VoiceEngine;
  onConfirm: () => void;
}

export default function VoiceEngineUpdateDetailsModal({
  open,
  onClose,
  engine,
  onConfirm
}: VoiceEngineUpdateDetailsModalProps) {
  const theme = useTheme();
  const friendlyName = engine.configuration?.friendly_name ?? 'Voice Engine';
  const currentVersion = engine.configuration?.version;
  const newVersion = engine.latest_configuration?.version ?? engine.latest_version;
  const release_date = engine.latest_configuration?.release_date;
  const improvementsValue = engine.latest_configuration?.improvements;
  const improvements = Array.isArray(improvementsValue)
    ? improvementsValue.filter((item) => typeof item === 'string' && item.trim())
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '650px',
          maxWidth: '100%',
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          position: 'relative'
        }
      }}
    >
      <IconButton
        aria-label='Close'
        onClick={onClose}
        size='small'
        sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
      >
        <IoClose size={22} />
      </IconButton>

      <DialogContent sx={{ pt: 3, px: { xs: 1, sm: 2 } }}>
        <Box display='flex' flexDirection='column' alignItems='center' textAlign='center' mb={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
              padding: 1.25
            }}
          >
            <SparkleIcon />
          </Box>
          <Typography fontWeight={600} fontSize={18} lineHeight={1.35}>
            <Typography component='span' color='primary.main' fontWeight={600}>
              {friendlyName}
            </Typography>{' '}
            <Typography component='span' color='text.primary' fontWeight={600}>
              Update Available: Version {newVersion}
            </Typography>
          </Typography>
        </Box>

        <Box
          display='grid'
          gridTemplateColumns='repeat(3, 1fr)'
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : '#EEF6FF',
            borderRadius: 1.5,
            py: 1.5,
            px: 1,
            mb: 2.5,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          {[
            { label: 'Current Version', value: currentVersion },
            { label: 'New Version', value: newVersion },
            { label: 'Release Date', value: release_date ?? '—' }
          ].map((cell, i) => (
            <Box
              key={cell.label}
              sx={{
                textAlign: 'center',
                borderRight: i < 2 ? `1px solid ${theme.palette.primary.main}` : 'none',
                px: 0.5
              }}
            >
              <Typography variant='caption' color='text.secondary' display='block' fontSize={12}>
                {cell.label}
              </Typography>
              <Typography fontWeight={400} fontSize={16} mt={0.25}>
                {cell.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {improvements.length > 0 && (
          <>
            <Typography fontWeight={500} fontSize={16} mb={1.5}>
              Engine Improvements
            </Typography>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#FAFAFA'
              }}
            >
              <Box component='ul' sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                {improvements.map((line, index) => (
                  <Box
                    key={index}
                    component='li'
                    sx={{
                      display: 'flex',
                      gap: 1.25,
                      alignItems: 'flex-start',
                      mb: 1.25,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box
                      sx={{
                        color: theme.palette.success.main,
                        mt: 0.15,
                        flexShrink: 0,
                        display: 'flex'
                      }}
                    >
                      <SiTicktick size={20} color='#22C55E' />
                    </Box>
                    <Typography variant='body2' color='text.secondary' fontSize={14} lineHeight={1.5}>
                      {line}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
        <Button variant='outlined' color='inherit' onClick={onClose} sx={{ textTransform: 'none', minWidth: 96 }}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' onClick={onConfirm} sx={{ textTransform: 'none' }}>
          Update to {newVersion}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
